/**
 * SEO — lightweight document.head manipulation for react-router SPAs.
 * Sets title + description + Open Graph + Twitter Card + canonical.
 */
import { useEffect } from "react";

function setTag(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    // selector shape: 'meta[NAME="VALUE"]'
    const m = selector.match(/^(\w+)\[(\w+)=["']?([^"'\]]+)["']?\]$/);
    if (!m) return;
    const [, tag, keyAttr, keyVal] = m;
    el = document.createElement(tag);
    el.setAttribute(keyAttr, keyVal);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value ?? "");
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href ?? "");
}

export function useSeo({
  title,
  description,
  image,
  url,
  type = "website",
  twitterCard = "summary_large_image",
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) setTag('meta[name="description"]', "content", description);
    if (title) setTag('meta[property="og:title"]', "content", title);
    if (description) setTag('meta[property="og:description"]', "content", description);
    if (image) setTag('meta[property="og:image"]', "content", image);
    if (url) setTag('meta[property="og:url"]', "content", url);
    setTag('meta[property="og:type"]', "content", type);
    setTag('meta[name="twitter:card"]', "content", twitterCard);
    if (title) setTag('meta[name="twitter:title"]', "content", title);
    if (description) setTag('meta[name="twitter:description"]', "content", description);
    if (image) setTag('meta[name="twitter:image"]', "content", image);
    if (url) setLink("canonical", url);
    return () => { document.title = prevTitle; };
  }, [title, description, image, url, type, twitterCard]);
}
